import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { Button } from '@clay-run/glaze/button';
import { Text } from '@clay-run/glaze/text';
import { noteTargetKey, normalizeAuthorName, type NoteEntry, type NoteTarget } from '@shared/flow/notes';
import { avatarColor, getInitials } from '../../../../view-system/utils/displayFormatters';
import type { NotesPending } from './flow-notes-store';

/** Rep-facing sentence for each state of the in-flight post. Never a status code. */
export function pendingStatusLabel(pending: Pick<NotesPending, 'status' | 'notConfirmed'>): string {
  switch (pending.status) {
    case 'saving':
      return 'Saving…';
    case 'awaiting':
      return pending.notConfirmed ? 'Not confirmed — Refresh' : 'Awaiting sync';
    case 'failed':
      return "Couldn't save — Retry";
    case 'unchanged':
      return 'This is already the latest note on this record.';
  }
}

/** Local time for a note: from `at` when known, otherwise the raw text Salesforce holds. */
function noteTime(entry: Pick<NoteEntry, 'at' | 'dateText'>): string | null {
  if (entry.at) {
    const when = new Date(entry.at);
    if (!Number.isNaN(when.getTime())) return when.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  }
  return entry.dateText;
}

/** A pending post belongs to a thread by account (contact and account threads alike): a sibling-contact post stays visible. */
function pendingBelongsToThread(pending: NotesPending, target: NoteTarget): boolean {
  if (noteTargetKey(pending.target) === noteTargetKey(target)) return true;
  return target.accountId !== null && pending.target.accountId === target.accountId;
}

export interface NotesChatProps {
  target: NoteTarget;
  /** Newest first, as the server returns them; shown newest at the bottom. */
  entries: NoteEntry[];
  /** The acting user's Salesforce display name; decides which bubbles are mine. */
  myName: string | null;
  pending: NotesPending | null;
  onRetry: () => void;
  onRefresh: () => void;
  /** The floating composer (rendered over the bottom of the pane). */
  composer: ReactNode;
  /**
   * Shown instead of the list while the thread is loading or failed to load. The chat (and so the
   * composer) stays mounted either way: unmounting the composer mid-post would drop its state.
   */
  placeholder?: ReactNode;
  /** Seam for U8a (mention chips); default is plain text. */
  renderBody?: (entry: NoteEntry) => ReactNode;
}

/**
 * The Notes thread: others left with avatar, name and time; mine right in a
 * brand-tinted bubble. The composer floats over the bottom edge, and the list
 * pads itself by the composer's measured height so the last note is never hidden.
 */
export function NotesChat({ target, entries, myName, pending, onRetry, onRefresh, composer, placeholder, renderBody }: NotesChatProps) {
  const ordered = [...entries].reverse();
  const visiblePending = pending && pendingBelongsToThread(pending, target) ? pending : null;
  const scrollRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const [composerHeight, setComposerHeight] = useState(0);

  useLayoutEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    const measure = () => setComposerHeight(el.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const targetKey = noteTargetKey(target);
  const pendingId = visiblePending?.clientNoteId ?? null;
  const pendingStatus = visiblePending?.status ?? null;
  // Open and post both land on the newest note.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [ordered.length, targetKey, pendingId, pendingStatus, composerHeight]);

  const isMine = (author: string | null) =>
    author !== null && myName !== null && (author === myName || author === normalizeAuthorName(myName));

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        data-testid="notes-scroll"
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
        style={{ paddingBottom: composerHeight }}
      >
        {placeholder ? (
          placeholder
        ) : ordered.length === 0 && !visiblePending ? (
          <Text size="xs" variant="tertiary" className="block py-6 text-center">
            No notes yet. Add the first one below.
          </Text>
        ) : (
          <div className="flex flex-col gap-3 pb-2 pt-1">
            {ordered.map(entry => (
              <NoteRow
                key={`${entry.recordKind}:${entry.recordId}:${entry.lineIndex}`}
                entry={entry}
                mine={isMine(entry.author)}
                scopeLabel={scopeLabelFor(entry, target)}
                renderBody={renderBody}
              />
            ))}
            {visiblePending && <PendingRow pending={visiblePending} onRetry={onRetry} onRefresh={onRefresh} />}
          </div>
        )}
      </div>
      <div
        ref={composerRef}
        className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-bg-primary from-70% to-transparent pt-6"
      >
        {composer}
      </div>
    </div>
  );
}

/** "Account" for an account note inside a contact thread; the contact's name for a contact note inside an account thread. */
function scopeLabelFor(entry: NoteEntry, target: NoteTarget): string | null {
  if (target.kind === 'contact') return entry.recordKind === 'account' ? 'Account' : null;
  return entry.recordKind === 'contact' ? entry.recordName : null;
}

function NoteRow({
  entry,
  mine,
  scopeLabel,
  renderBody,
}: {
  entry: NoteEntry;
  mine: boolean;
  scopeLabel: string | null;
  renderBody?: (entry: NoteEntry) => ReactNode;
}) {
  const time = noteTime(entry);
  const name = entry.author ?? '';
  return (
    <div data-testid="note-row" data-mine={mine} className={clsx('flex flex-col gap-1', mine ? 'items-end pl-8' : 'items-start pr-8')}>
      <div className={clsx('flex max-w-full items-end gap-2', mine && 'flex-row-reverse')}>
        {!mine && (
          <span
            aria-hidden
            className={clsx(
              'flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-content-inverse',
              avatarColor(name),
            )}
          >
            {getInitials(name)}
          </span>
        )}
        <div className="flex min-w-0 flex-col gap-0.5">
          {!mine && (
            <div className="flex items-baseline gap-1.5">
              {name && (
                <Text size="xs" weight="medium">
                  {name}
                </Text>
              )}
              {time && (
                <Text size="xs" variant="tertiary">
                  {time}
                </Text>
              )}
            </div>
          )}
          {scopeLabel && (
            <Text size="xs" variant="tertiary">
              {scopeLabel}
            </Text>
          )}
          <div className={clsx('rounded-2xl px-3 py-2', mine ? 'rounded-br-md bg-bg-blue-light' : 'rounded-bl-md bg-bg-secondary')}>
            <Text size="sm" className="break-words">
              {renderBody ? renderBody(entry) : entry.body}
            </Text>
          </div>
          {mine && time && (
            <Text size="xs" variant="tertiary" className="self-end">
              {time}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
}

function PendingRow({ pending, onRetry, onRefresh }: { pending: NotesPending; onRetry: () => void; onRefresh: () => void }) {
  const label = pendingStatusLabel(pending);
  const notConfirmed = pending.status === 'awaiting' && pending.notConfirmed === true;
  return (
    <div data-testid="note-pending" className="flex flex-col items-end gap-1 pl-8">
      <div className="rounded-2xl rounded-br-md bg-bg-blue-light px-3 py-2">
        <Text size="sm" className="break-words">
          {pending.body}
        </Text>
      </div>
      <div className="flex flex-col items-end gap-1">
        {pending.status === 'failed' && pending.error && (
          <Text size="xs" variant="critical">
            {pending.error.title}
          </Text>
        )}
        {pending.status === 'failed' && pending.error?.oauthUrl && (
          <a href={pending.error.oauthUrl} className="text-xs font-semibold text-content-action underline">
            Reconnect Salesforce
          </a>
        )}
        {pending.status === 'failed' || notConfirmed ? (
          <Button size="sm" variant="ghost" onClick={pending.status === 'failed' ? onRetry : onRefresh}>
            {label}
          </Button>
        ) : (
          <Text size="xs" variant="tertiary">
            {label}
          </Text>
        )}
      </div>
    </div>
  );
}
